import React, { type ReactNode, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import type { PopupProps } from '@atlaskit/popup';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { PopupTriggerWithHover as PopupTriggerWithHoverFromPackage } from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';
import { layers } from '@atlaskit/theme/constants';

import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import {
	PopupTriggerWithHover as PopupTriggerWithHoverLocal,
	type TriggerMode,
} from '../popup-adapter/PopupTriggerWithHover';
import { getPageTime } from '../utils/performance';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { UserProfileCardTrigger } from './components/UserProfileCardTrigger';

/**
 * Props for {@link UserProfileCardWithTrigger}. Extends {@link UserProfileCardRelayTriggerParams}
 * with popup placement, trigger mode, render target, and the trigger element children.
 */
export type UserProfileCardWithTriggerProps = UserProfileCardRelayTriggerParams &
	Pick<PopupProps, 'placement'> & {
		/** Hover, click, or preload-on-hover. Defaults to `'click'`. */
		trigger?: TriggerMode;
		/** When `true`, renders the popup into the document body via a portal. */
		shouldRenderToParent?: boolean;
		/** Element that the popup attaches to (the visible trigger). */
		children: ReactNode;
		/** Test id forwarded to the popup root. */
		testId?: string;
	};

/**
 * Opens the user profile card in a popup (hover / click / preload) using the same Relay entry-point
 * pattern as `TeamProfileCardWithTrigger` (`@atlassian/team-profilecard/trigger`): preload runs
 * `UserProfileCardEntryPointQuery` (`knowledgeDiscovery.singleUser` + `activity.workedOn`).
 *
 * For a presentational card with data you already have, use {@link UserProfileCard} directly.
 */
export function UserProfileCardWithTrigger({
	userId,
	cloudId,
	orgId,
	viewerId,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	children,
	testId,
}: UserProfileCardWithTriggerProps): React.JSX.Element {
	const PopupTriggerWithHover = fg('people-teams_migrate-popup-adaptor-package')
		? PopupTriggerWithHoverFromPackage
		: PopupTriggerWithHoverLocal;
	const resolvedTriggerMode = trigger ?? 'click';

	const onErrorBoundary = useCallback(() => {
		void getPageTime();
	}, []);

	// TODO update to use error and onClose props from the error boundary, once CardError is updated to accept these props
	// needed by the type of the InternalEntryPointContainer's errorFallback prop
	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	// `orgId` is required by both the entry-point and the new
	// `@atlaskit/teams-app-internal-popup-adaptor` PopupTriggerWithHover. If any
	// required relay param is missing, render the trigger child untouched so the
	// caller's UI is not broken.
	if (!userId || !cloudId || !orgId || !viewerId) {
		void getPageTime();
		return <>{children}</>;
	}

	return (
		<ErrorBoundary onError={onErrorBoundary} fallback={<>{children}</>}>
			<TeamsAppAnalyticsContext data={{ source: 'userProfileCard' }}>
				<UserProfileCardPopupWithAnalytics
					PopupTriggerWithHover={PopupTriggerWithHover}
					entryPointParams={{ userId, cloudId, orgId, viewerId }}
					entryPointProps={{ cloudId, orgId, viewerId, testId }}
					placement={placement}
					shouldRenderToParent={shouldRenderToParent}
					triggerMode={resolvedTriggerMode}
					errorFallback={entryPointErrorFallback}
					trigger={(triggerProps) => (
						<UserProfileCardTrigger trigger={resolvedTriggerMode} triggerProps={triggerProps}>
							{children}
						</UserProfileCardTrigger>
					)}
				/>
			</TeamsAppAnalyticsContext>
		</ErrorBoundary>
	);
}

function UserProfileCardPopupWithAnalytics({
	PopupTriggerWithHover,
	entryPointParams,
	entryPointProps,
	placement,
	shouldRenderToParent,
	triggerMode,
	errorFallback,
	trigger,
}: {
	/**
	 * The active `PopupTriggerWithHover` implementation, resolved by the parent
	 * based on the `people-teams_migrate-popup-adaptor-package` feature gate.
	 * Passing this in (rather than re-deriving it here) keeps a single source of
	 * truth for the gate lookup.
	 */
	PopupTriggerWithHover:
		| typeof PopupTriggerWithHoverFromPackage
		| typeof PopupTriggerWithHoverLocal;
	entryPointParams: UserProfileCardRelayTriggerParams;
	entryPointProps: { cloudId: string; orgId: string; viewerId: string; testId?: string };
	placement: PopupProps['placement'];
	shouldRenderToParent: boolean;
	triggerMode: TriggerMode;
	errorFallback: (props: { error: Error; onClose: () => void }) => React.JSX.Element;
	trigger: React.ComponentProps<
		typeof PopupTriggerWithHoverFromPackage | typeof PopupTriggerWithHoverLocal
	>['trigger'];
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const handleOpen = useCallback(
		(method: 'click' | 'hover') => {
			fireEvent('ui.userProfileCard.opened', { triggerMethod: method });
		},
		[fireEvent],
	);

	const handleClose = useCallback(() => {
		fireEvent('ui.userProfileCard.closed', {});
	}, [fireEvent]);

	return (
		<PopupTriggerWithHover
			entryPoint={entryPoint}
			placement={placement}
			fallback={<CardLoading />}
			errorFallback={errorFallback}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			shouldRenderToParent={shouldRenderToParent}
			shouldUseCaptureOnOutsideClick={true}
			zIndex={layers.modal()}
			triggerMode={triggerMode}
			onOpen={handleOpen}
			onClose={handleClose}
			trigger={trigger}
		/>
	);
}
